import type { DocPageContent } from '../docs-content'

const page = {
  order: 1,
  slug: 'getting-started',
  title: '快速入门',
  description:
    '精剪是什么、浏览器需要什么条件，以及从启动到导出的第一次剪辑。',
  category: '入门',
  related: ['concepts', 'workspaces', 'export'],
  sections: [
    {
      title: '精剪是什么',
      blocks: [
        {
          kind: 'paragraph',
          text: '精剪是一款**本地优先**的视频编辑器，完全在浏览器中运行——无需账号、上传或服务器渲染。剪辑、特效、调色、AI 工具与导出全部在你的本机上借助 GPU 和 CPU 完成。',
        },
        {
          kind: 'note',
          tone: 'tip',
          text: '你的媒体始终保留在设备上。精剪会链接到原始文件，并把项目、缓存和导出写入你选择的**工作区文件夹**——没有任何内容被发送到云端。',
        },
      ],
    },
    {
      title: '浏览器需要什么条件',
      blocks: [
        {
          kind: 'paragraph',
          text: '精剪依赖现代 Web API，因此受支持的方式是使用较新版本的 Chromium 内核浏览器。',
        },
        {
          kind: 'table',
          headers: ['浏览器', '状态'],
          rows: [
            ['Chrome / Edge 113+', '完全支持'],
            ['Brave', '启用 File System Access API 标志后可用'],
            ['Safari / Firefox', '尚不支持完整工作流'],
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: '精剪使用 File System Access API（工作区文件夹）、WebCodecs（解码与导出）、WebGPU（特效、调色、AI）以及 OPFS（缓存）。请保持硬件加速开启，并及时更新 GPU 驱动。',
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'Brave 可能会拦截文件夹访问。打开 `brave://flags/#file-system-access-api`，将其设置为**Enabled**，然后重新启动 Brave。',
        },
      ],
    },
    {
      title: '你的第一次剪辑',
      blocks: [
        {
          kind: 'steps',
          items: [
            '在受支持的浏览器中打开精剪，并在出现提示时选择一个可以读写的**工作区文件夹**。',
            '在“项目”页面选择**新建项目**，并为剪辑设置分辨率与帧率。',
            '打开**媒体**标签页，使用**导入**添加文件，或将媒体直接拖入素材库。',
            '将一段片段从媒体面板拖到时间轴轨道上。',
            '按 `Space` 播放，使用 `Left` 和 `Right` 逐帧步进。',
            '工作时用 `Ctrl+S` 保存，然后选择**导出**渲染成品视频。',
          ],
        },
      ],
    },
    {
      title: '确认你的环境正常',
      blocks: [
        {
          kind: 'list',
          items: [
            '工作区选择器可以打开并接受普通文件夹（非受保护的系统位置）。',
            '导入的媒体以带缩略图的卡片形式出现在媒体面板中。',
            '在时间轴中按 `Space` 即可开始播放。',
            '导出功能可以打开，且预检未报告任何阻塞性问题。',
          ],
        },
        {
          kind: 'note',
          tone: 'tip',
          text: '如果某一步失败，请查看**故障排除**页面中对应的症状说明。',
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page
